/**
 * @file NotFound404.tsx
 *
 * @description This component renders a user-friendly error page when users attempt to access
 * non-existent routes or resources, providing navigation options back to valid application areas.
 *
 * @module Views
 * @requires React
 * @requires Material-UI
 * @requires react-router-dom
 */
import React from "react";
import { Box, Typography, Button, Container, useTheme } from "@mui/material";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import SquareLogo from "../../assets/logo512.svg";
import SquareLogoLight from "../../assets/logo512light.svg";

// Styled container component that uses flexbox and centers its content both horizontally and vertically.
const StyledContainer = styled(Container)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  minHeight: "calc(100vh - 100px)",
}));

// Styled image component for the 404 page logo
const Image = styled("img")({
  maxWidth: "100%",
  height: "auto",
  marginBottom: "2rem",
  width: "100px",
  opacity: 0.5,
  filter: "grayscale(100%)",
});

/**
 * @name NotFound404
 *
 * @description Displays a user-friendly error message when officers attempt to access a non-existent route.
 * Provides navigation options to return to the application home page or contact support.
 *
 * @returns {React.ReactElement} The rendered 404 Not Found page
 */
const NotFound404: React.FC = () => {
  const theme = useTheme();

  return (
    <StyledContainer maxWidth="xl">
      <Box>
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontSize: "4rem",
            fontWeight: 800,
            mb: 2,
          }}
        >
          404
        </Typography>

        {/* Application logo with styling for error context */}
        <Image
          src={theme.palette.mode === "dark" ? SquareLogoLight : SquareLogo}
          alt="404 Image"
          width={500}
          height={350}
        />

        {/* Primary error message with Star Wars reference */}
        <Typography
          variant="h2"
          sx={{
            fontSize: "1.5rem",
            fontWeight: 500,
            mb: 2,
          }}
        >
          This is not the web page you're looking for.
        </Typography>

        {/* Secondary explanatory message */}
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          The page you are trying to access might have been moved, deleted, or
          does not exist.
        </Typography>

        {/* Action buttons container */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button
            variant="contained"
            component={Link}
            to="/"
            sx={{
              textTransform: "none",
              backgroundColor: "#2ea44f",
              "&:hover": {
                backgroundColor: "#238636",
              },
            }}
          >
            Go to Home
          </Button>

          {/* Secondary action button to contact IT support */}
          <Button
            variant="outlined"
            component="a"
            href="mailto:admin@kardanalytics.com"
            sx={{
              textTransform: "none",
              borderColor: "text.primary",
              borderWidth: 1,
              color: "text.primary",
              "&:hover": {
                borderColor: "text.primary",
                borderWidth: 1,
              },
            }}
          >
            Contact Support
          </Button>
        </Box>
      </Box>
    </StyledContainer>
  );
};

export default NotFound404;
